import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "react-quill/dist/quill.snow.css";

// Contextos
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { SessionProvider } from "./contexts/SessionContext";
import { CartProvider } from "./contexts/CartContext";

// Componentes de Layout e Comuns
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import ToastSystem from "./components/common/ToastSystem";
import PrivateRoute from "./components/common/PrivateRoute";
import MaintenanceBlocker from "./components/common/MaintenanceBlocker";
import MainLayout from "./pages/MainLayout";

// Páginas Públicas
import HomeInstitutional from "./pages/HomeInstitutional";
import PublicPage from "./pages/PublicPage";
import ForumPublic from "./pages/ForumPublic";
import PublicationsPage from "./pages/PublicationsPage";
import CoursesCatalog from "./pages/CoursesCatalog";
import GalleryPage from "./pages/GalleryPage";
import ProductDetails from "./pages/ProductDetails";
import PublicCoursePreview from "./pages/PublicCoursePreview";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import NotFoundPage from "./pages/NotFoundPage";

// ⭐ Nova Página de Validação
import CertificateValidation from "./pages/CertificateValidation";

// Páginas de Usuário (Privadas)
import DashboardPage from "./pages/DashboardPage";
import ProgressPage from "./pages/ProgressPage";
import CompletedPage from "./pages/CompletedPage";
import UserProfile from "./pages/UserProfile";
import CoursePage from "./pages/CoursePage";
import Checkout from "./pages/Checkout";

// Páginas de Admin/Professor (Privadas)
import AdminDashboard from "./pages/AdminDashboard";
import ProfessorDashboard from "./pages/ProfessorDashboard";
import AdminProducts from "./pages/AdminProducts";
import PageBuilder from "./components/admin/PageBuilder";

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <SessionProvider>
          <SettingsProvider>
            <ThemeProvider>
              <CartProvider>
                <MaintenanceBlocker>
                  <ToastSystem />

                  <MainLayout>
                    <Header />

                    <main className="min-h-screen">
                      <Routes>
                        {/* ================= HOME ================= */}
                        <Route path="/" element={<HomeInstitutional />} />
                        <Route path="/home" element={<HomeInstitutional />} />

                        {/* ================= FÓRUM PÚBLICO ================= */}
                        <Route path="/forum" element={<ForumPublic />} />

                        {/* ================= GALERIA ================= */}
                        <Route path="/galeria" element={<GalleryPage />} />

                        {/* ================= PRODUTOS ================= */}
                        <Route path="/produto/:id" element={<ProductDetails />} />
                        
                        {/* ================= VALIDAR CERTIFICADO (Público) ================= */}
                        <Route path="/validar-certificado/:hash" element={<CertificateValidation />} />

                        {/* ================= PUBLICAÇÕES ================= */}
                        <Route
                          path="/publicacoes/:id"
                          element={
                            <PrivateRoute>
                              <PublicationsPage />
                            </PrivateRoute>
                          }
                        />

                        {/* ================= CATÁLOGO ================= */}
                        <Route path="/cursos" element={<CoursesCatalog />} />

                        {/* ================= CHECKOUT ================= */}
                        <Route
                          path="/checkout"
                          element={
                            <PrivateRoute>
                              <Checkout />
                            </PrivateRoute>
                          }
                        />

                        {/* ================= CMS PÚBLICO ================= */}
                        <Route path="/p/:slug" element={<PublicPage />} />

                        {/* ================= CURSOS ================= */}
                        <Route
                          path="/curso-publico/:id"
                          element={<PublicCoursePreview />}
                        />

                        <Route
                          path="/cursos/:courseId"
                          element={
                            <PrivateRoute>
                              <CoursePage />
                            </PrivateRoute>
                          }
                        />

                        {/* ================= AUTH ================= */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/cadastro" element={<RegisterPage />} />

                        {/* ================= USUÁRIO ================= */}
                        <Route
                          path="/perfil"
                          element={
                            <PrivateRoute>
                              <UserProfile />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/dashboard"
                          element={
                            <PrivateRoute>
                              <DashboardPage />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/progresso"
                          element={
                            <PrivateRoute>
                              <ProgressPage />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/concluidos"
                          element={
                            <PrivateRoute>
                              <CompletedPage />
                            </PrivateRoute>
                          }
                        />

                        {/* ================= PROFESSOR ================= */}
                        <Route
                          path="/professor"
                          element={
                            <PrivateRoute requiredRoles={["professor", "supervisor", "admin"]}>
                              <ProfessorDashboard />
                            </PrivateRoute>
                          }
                        />

                        {/* ================= ADMIN ================= */}
                        <Route
                          path="/admin"
                          element={
                            <PrivateRoute requiredRoles={["admin", "supervisor"]}>
                              <AdminDashboard />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/admin/produtos"
                          element={
                            <PrivateRoute requiredRoles={["admin"]}>
                              <AdminProducts />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/admin/pages/:slug"
                          element={
                            <PrivateRoute requiredRoles={["admin", "supervisor"]}>
                              <PageBuilder />
                            </PrivateRoute>
                          }
                        />

                        {/* ================= 404 ================= */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </main>

                    <Footer />
                  </MainLayout>
                </MaintenanceBlocker>
              </CartProvider>
            </ThemeProvider>
          </SettingsProvider>
        </SessionProvider>
      </AuthProvider>
    </Router>
  );
}